/**
 * StrategicAngleCard — one card per ContentStrategy.strategicAngle[i].
 *
 * Inline edit for `description` (free text) and `anchorInsightIds`
 * (comma-separated string ⇄ string[]). themeIds is read-only here —
 * theme assignment lives in the SprintDraftBlock theme-quota table.
 *
 * CRITICAL persistence pattern (Firestore footgun):
 *   `strategicAngle` is an ARRAY field. Firestore dot-notation CANNOT
 *   update an array element by index — `"strategicAngle.0.description"`
 *   silently does nothing. So the parent passes WHOLE strategicAngle[]
 *   to patchStrategyDraft({ strategicAngle: [...edited] }). This card
 *   only manages local state + calls onSave(updatedAngle) — the parent
 *   replays the array.
 */

import React, { useState } from 'react';
import {
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

export interface StrategicAngleShape {
  id: string;
  name: string;
  description: string;
  themeIds: string[];
  anchorInsightIds: string[];
}

export interface StrategicAngleCardProps {
  angle: StrategicAngleShape;
  index: number;
  onSave: (next: StrategicAngleShape) => Promise<void>;
  disabled?: boolean;
}

const splitCsv = (s: string): string[] =>
  s.split(',').map((x) => x.trim()).filter(Boolean);

export const StrategicAngleCard: React.FC<StrategicAngleCardProps> = ({
  angle,
  index,
  onSave,
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftDescription, setDraftDescription] = useState(angle.description ?? '');
  const [draftAnchorIds, setDraftAnchorIds] = useState((angle.anchorInsightIds ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state when the upstream angle changes between edits.
  React.useEffect(() => {
    if (!editing) {
      setDraftDescription(angle.description ?? '');
      setDraftAnchorIds((angle.anchorInsightIds ?? []).join(', '));
    }
  }, [angle.description, angle.anchorInsightIds, editing]);

  const handleStartEdit = () => {
    setError(null);
    setDraftDescription(angle.description ?? '');
    setDraftAnchorIds((angle.anchorInsightIds ?? []).join(', '));
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    const description = draftDescription.trim();
    if (!description) {
      setError('Description cannot be empty.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...angle,
        description,
        anchorInsightIds: splitCsv(draftAnchorIds),
      });
      setEditing(false);
    } catch (e) {
      setError((e as Error).message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      data-testid={`strategic-angle-card-${angle.id}`}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip size="small" label={`#${index + 1}`} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {angle.name}
            </Typography>
          </Stack>
          {editing ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Description"
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={saving}
                inputProps={{ 'data-testid': `strategic-angle-description-${angle.id}` }}
              />
              <TextField
                label="Anchor insight ids (comma-separated)"
                value={draftAnchorIds}
                onChange={(e) => setDraftAnchorIds(e.target.value)}
                fullWidth
                disabled={saving}
                helperText="At least one required. Use the insight ids exactly as they appear in the analysis."
                inputProps={{ 'data-testid': `strategic-angle-anchors-${angle.id}` }}
              />
              {error && (
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  data-testid={`strategic-angle-save-${angle.id}`}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<CloseIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {angle.description}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {(angle.themeIds ?? []).map((tid) => (
                  <Chip key={`theme-${tid}`} size="small" variant="outlined" label={`theme: ${tid}`} />
                ))}
                {(angle.anchorInsightIds ?? []).map((aid) => (
                  <Chip
                    key={`anchor-${aid}`}
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`@${aid}`}
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
        {!editing && (
          <IconButton
            size="small"
            onClick={handleStartEdit}
            disabled={disabled}
            aria-label="Edit strategic angle"
            data-testid={`strategic-angle-edit-${angle.id}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
};

export default StrategicAngleCard;
